import { Fragment, h } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { IDrawer } from "@theme/drawer/drawer";
import { IAddToCartNotification } from "@snippets/add-to-cart-notification";
import { ICart, CartData } from "@sections/cart";

import MiniCartHeaderButton from "./MiniCartHeaderButton";
import MiniCartHeader from "./MiniCartHeader";
import MiniCartItem from "./MiniCartItem";
import MiniCartTotals from "./MiniCartTotals";
import MiniCartFooter from "./MiniCartFooter";
import MiniCartThresholdMessage from "./MiniCartThresholdMessage";
import MiniCartRecommendations from "./MiniCartRecommendations";
import MiniCartFreeGift from "./MiniCartFreeGift";

interface MiniCartProps {
  "data-text-color"?: string;
  open?: string;
}

async function fetchProductMetafieldsById(productId: string): Promise<any[]> {
  const endpoint = `https://${window.themeVars.store.permanent_domain}/api/${window.themeVars.config.storefrontApiVersion}/graphql`;

  const query = `
  query ProductMetafields($id: ID!) {
    product(id: $id) {
      id
      handle
      title
      metafields(identifiers: [{namespace: "custom", key: "pre_order_information"}]) {
        key
        value
        reference {
          ... on Metaobject {
            id
            type
            fields {
              key
              value
            }
          }
        }
      }
    }
  }
  `;

  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token":
        window.themeVars.config.storefrontAccessToken,
    },
    body: JSON.stringify({
      query,
      variables: { id: productId },
    }),
  });

  const preOrderData = await response.json();

  return preOrderData?.data?.product || [];
}

function MiniCart(props: MiniCartProps) {
  const isWhiteMode = props["data-text-color"] === "white";

  const [cartData, setCartData] = useState<CartData | null>(null);
  const cartRef = useRef<ICart>(document.querySelector("uwp-cart"));

  const miniCartDrawerRef = useRef<IDrawer | null>(null);
  const addToCartNotficationRef = useRef<IAddToCartNotification | null>(null);

  const recommendationProductHandle = cartData?.items[0]?.handle ?? "";

  const formattedDeliveryMessaging =
    window.themeVars.config.cart.deliveryMessaging
      .replace("<p>", '<p class="text-sm-body">')
      .replace("<strong>", '<strong class="text-sm-body--semi-bold">');

  const enableMinimumCartThreshold =
    window.themeVars.config.cart.enableMinimumCartThreshold;
  const minimumCartThreshold =
    window.themeVars.config.cart.minimumCartThreshold;

  let showMinimumCartThresholdMessage = false;
  let showViewBasketButton = true;

  const originalTotalPrice = cartData?.original_total_price ?? 0;

  if (enableMinimumCartThreshold && originalTotalPrice < minimumCartThreshold) {
    showMinimumCartThresholdMessage = true;
    showViewBasketButton = false;
  }

  if (
    enableMinimumCartThreshold &&
    originalTotalPrice >= minimumCartThreshold
  ) {
    showMinimumCartThresholdMessage = false;
    showViewBasketButton = true;
  }

  if (!enableMinimumCartThreshold) {
    showViewBasketButton = true;
  }

  useEffect(() => {
    const drawerElement = document.querySelector(
      "uwp-drawer[modal-name=mini-cart]",
    );

    if (drawerElement) {
      miniCartDrawerRef.current = drawerElement as IDrawer;
    }

    const addToCartNotificationElement = document.querySelector(
      "uwp-add-to-cart-notification",
    );

    if (addToCartNotificationElement) {
      addToCartNotficationRef.current =
        addToCartNotificationElement as IAddToCartNotification;
    }
  }, []);

  useEffect(() => {
    const cartElement = cartRef.current;

    if (!cartElement) {
      return;
    }

    setCartData(cartElement.cartData);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "cart-data"
        ) {
          const updatedData = (mutation.target as ICart).cartData;

          setCartData(updatedData);
        }
      });
    });

    observer.observe(cartElement, { attributes: true });
    return () => observer.disconnect();
  }, []);

  document.addEventListener("cart:updated", (event: Event) => {
    const customEvent = event as CustomEvent;

    setCartData(customEvent.detail);
  });

  document.addEventListener("cart:added", (event: Event) => {
    const customEvent = event as CustomEvent;

    if (!addToCartNotficationRef.current) {
      return;
    }

    if (customEvent.detail?.token || customEvent.detail?.id) {
      addToCartNotficationRef.current.showNotification();
    }
  });

  const openDrawer = () => {
    if (window.themeVars.store.template === "cart") {
      return;
    }

    // Close any other open modals/drawers before opening minicart
    const openModals = document.querySelectorAll(
      '.modal.is-open, .drawer.is-open:not([modal-name="mini-cart"]), .desktop-menu__item.is-open',
    );
    openModals.forEach((modal) => {
      if (modal.classList.contains("desktop-menu__item")) {
        // Close desktop menu
        modal.classList.remove("is-open");
        const desktopMenu = modal.closest("uwp-desktop-menu") as any;
        if (desktopMenu?.closeAllDropdowns) {
          desktopMenu.closeAllDropdowns();
        }
      } else if (
        modal.classList.contains("modal") ||
        modal.classList.contains("drawer")
      ) {
        // Close other modals/drawers
        const hideMethod =
          (modal as any).hideModal || (modal as any).hideDrawer;
        if (hideMethod) {
          hideMethod.call(modal);
        }
      }
    });

    if (miniCartDrawerRef.current) {
      miniCartDrawerRef.current.showDrawer();
    } else {
      console.error("miniCartDrawerRef.current is null");
    }
  };

  const closeDrawer = () => {
    const htmlElement = document.querySelector("html");
    if (htmlElement) {
      htmlElement.style.overflow = ""; // Set the overflow style back to its original state
      htmlElement.style.paddingRight = "";
    } else {
      console.error("HTML element not found");
    }

    if (miniCartDrawerRef.current) {
      miniCartDrawerRef.current.hideDrawer();
    } else {
      console.error("miniCartDrawerRef.current is null");
    }
  };

  const increaseQuantity = (itemId: string, quantity: number, max: number) => {
    if (!cartRef.current) {
      return;
    }

    if (quantity >= max) {
      return;
    }
    cartRef.current.changeItemQuantity(itemId, quantity + 1);
  };

  const decreaseQuantity = (itemId: string, quantity: number, min: number) => {
    if (!cartRef.current) {
      return;
    }

    if (quantity <= min) {
      return;
    }
    cartRef.current.changeItemQuantity(itemId, quantity - 1);
  };

  const removeItem = (itemId: string) => {
    if (!cartRef.current) {
      return;
    }

    cartRef.current.changeItemQuantity(itemId, 0);
  };

  const addItem = (itemId: string) => {
    if (!cartRef.current) {
      return;
    }

    cartRef.current.addItem(itemId);
  };

  // (Removed unused total item count calculation)

  const [cartMetafields, setCartMetafields] = useState<
    { itemId: string; metafields: any }[]
  >([]);

  useEffect(() => {
    if (!cartData?.items?.length) {
      setCartMetafields([]);
      return;
    }

    async function fetchAllMetafields() {
      const items = cartData!.items;
      const allMetafields: any[] = await Promise.all(
        items.map(async (item) => {
          const productGid = `gid://shopify/Product/${item.product_id}`;
          const metafields = await fetchProductMetafieldsById(productGid);
          return { itemId: item.id, metafields };
        }),
      );
      setCartMetafields(allMetafields);
    }

    fetchAllMetafields();
  }, [cartData]);

  const hasPreOrderItems = !!cartData?.items?.some((item) => {
    if (item.title.includes("Pfand")) return false;
    const metafieldsEntry = cartMetafields.find(
      (entry) => entry.metafields?.handle === item.handle,
    );
    const referenceFields =
      metafieldsEntry?.metafields?.metafields?.[0]?.reference?.fields || [];
    const enablePreOrderField = referenceFields.find(
      (field: any) => field.key === "enable_pre_order",
    );
    const isPreOrder =
      enablePreOrderField?.value === true ||
      enablePreOrderField?.value === "true";
    return !!isPreOrder;
  });

  const hasNonPreOrderItems = !!cartData?.items?.some((item) => {
    if (item.title.includes("Pfand")) return false;
    const metafieldsEntry = cartMetafields.find(
      (entry) => entry.metafields?.handle === item.handle,
    );
    const referenceFields =
      metafieldsEntry?.metafields?.metafields?.[0]?.reference?.fields || [];
    const enablePreOrderField = referenceFields.find(
      (field: any) => field.key === "enable_pre_order",
    );
    const isPreOrder =
      enablePreOrderField?.value === true ||
      enablePreOrderField?.value === "true";
    return !isPreOrder;
  });

  return (
    <div
      className={`
        minicart__container
        ${!cartData?.item_count ? "is-empty" : ""}
      `}
    >
      <MiniCartHeaderButton
        openDrawer={openDrawer}
        cartData={cartData}
        isWhiteMode={isWhiteMode}
      />

      {window.themeVars.store.template !== "cart" && (
        <uwp-drawer
          modal-name="mini-cart"
          className="drawer minicart__drawer drawer--right"
          aria-hidden="true"
          drawer-direction="right"
          ref={miniCartDrawerRef}
        >
          <div className="modal__overlay" tabIndex={-1} data-close-drawer></div>

          <aside className="drawer__container" role="dialog" aria-modal="true">
            <div className="minicart__main">
              <MiniCartHeader closeDrawer={closeDrawer} />

              {showMinimumCartThresholdMessage ? (
                <MiniCartThresholdMessage />
              ) : (
                <uwp-free-shipping-indicator
                  cart-value={cartData?.total_price}
                  show-success-message="true"
                  text-class="text-sm-body--semi-bold"
                ></uwp-free-shipping-indicator>
              )}

              <div className="minicart__body" js-modal="scrollElement">
                {window.themeVars.config.cart.deliveryMessaging && (
                  <div
                    className="
                      minicart__delivery-messaging
                      text-sm-body
                    "
                    dangerouslySetInnerHTML={{
                      __html: formattedDeliveryMessaging,
                    }}
                  ></div>
                )}

                {hasPreOrderItems && (
                  <div className="minicart__items-list">
                    <p className="cart-upsell__title heading-4">
                      {window.themeVars.strings.cart.preOrder}
                    </p>
                    {cartData?.items.map((item) => {
                      if (item.title.includes("Pfand")) return null;

                      const metafieldsEntry = cartMetafields.find(
                        (entry) => entry.metafields?.handle === item.handle,
                      );

                      const referenceFields =
                        metafieldsEntry?.metafields?.metafields?.[0]?.reference
                          ?.fields || [];

                      const enablePreOrderField = referenceFields.find(
                        (field: any) => field.key === "enable_pre_order",
                      );
                      const isPreOrder =
                        enablePreOrderField?.value === true ||
                        enablePreOrderField?.value === "true";
                      if (!isPreOrder) return null;

                      return (
                        <MiniCartItem
                          methods={{
                            increaseQuantity,
                            decreaseQuantity,
                            removeItem,
                          }}
                          key={item.id}
                          item={item}
                          metafields={referenceFields}
                        />
                      );
                    })}
                  </div>
                )}

                <div className="minicart__items-list">
                  {hasPreOrderItems && hasNonPreOrderItems && (
                    <p className="cart-upsell__title heading-4">
                      {window.themeVars.strings.cart["deliverNow"]}
                    </p>
                  )}
                  {cartData?.items.map((item) => {
                    if (item.title.includes("Pfand")) return null;

                    const metafieldsEntry = cartMetafields.find(
                      (entry) => entry.metafields?.handle === item.handle,
                    );

                    const referenceFields =
                      metafieldsEntry?.metafields?.metafields?.[0]?.reference
                        ?.fields || [];

                    const enablePreOrderField = referenceFields.find(
                      (field: any) => field.key === "enable_pre_order",
                    );
                    const isPreOrder =
                      enablePreOrderField?.value === true ||
                      enablePreOrderField?.value === "true";
                    if (isPreOrder) return null;

                    return (
                      <MiniCartItem
                        methods={{
                          increaseQuantity,
                          decreaseQuantity,
                          removeItem,
                        }}
                        key={item.id}
                        item={item}
                      />
                    );
                  })}
                </div>

                {cartData && (
                  <Fragment>
                    <MiniCartFreeGift />

                    {window.themeVars.config.enableCartRecommendations && (
                      <MiniCartRecommendations
                        addItem={addItem}
                        recommendationProductHandle={
                          recommendationProductHandle
                        }
                      />
                    )}
                  </Fragment>
                )}

                <MiniCartTotals cartData={cartData} />
              </div>

              <div className="minicart__body--empty">
                <p className="minicart__message">
                  {window.themeVars.strings.cart.cartEmpty}
                </p>
              </div>

              <MiniCartFooter
                closeDrawer={closeDrawer}
                showMinimumCartThresholdMessage={
                  showMinimumCartThresholdMessage
                }
                showViewBasketButton={showViewBasketButton}
              />
            </div>
          </aside>
        </uwp-drawer>
      )}
    </div>
  );
}

export default MiniCart;
